import React from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLayers, FiShield, FiTarget, FiBox, FiCpu, FiInfo, FiBriefcase } = FiIcons;

const StrategicOverview = () => {
  const frameworkSteps = [
    { title: "Structureren", desc: "Conversie van ongestructureerde CV-gegevens naar een genormaliseerd profiel." },
    { title: "Analyseren", desc: "Extractie van kernvereisten en contextuele signalen uit vacatureteksten." },
    { title: "Vergelijken", desc: "Objectieve toetsing van profielkenmerken tegen de gedefinieerde rol-context." },
    { title: "Benoemen", desc: "Identificatie van feitelijke overeenkomsten en relevante hiaten." },
    { title: "Toelichten", desc: "Vertaling van de analyse naar transparante, begrijpelijke feedback." }
  ];

  const targetGroups = [
    { name: "Opleiders en onderwijsinstellingen", context: "Ondersteuning bij loopbaanoriëntatie en curriculum-matching." },
    { name: "HR-bureaus en recruitmentorganisaties", context: "Optimalisatie van de eerste selectie en kwaliteitsborging." },
    { name: "Arbeidsmarktplatforms en jobboards", context: "Verrijking van de gebruikerservaring met matching-intelligentie." },
    { name: "Publieke organisaties", context: "Inzet binnen werkgelegenheidsprojecten en herplaatsingstrajecten." }
  ];

  return (
    <div className="min-h-screen py-16 lg:py-24 bg-white selection:bg-brand-orange selection:text-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header Section */}
        <header className="mb-16 border-b border-gray-100 pb-12 text-center md:text-left">
          <h1 className="text-3xl lg:text-4xl font-bold text-brand-dark mb-6 tracking-tight">
            Over Vacature.ai – methodiek & samenwerking
          </h1>
          <p className="text-gray-500 text-sm font-medium uppercase tracking-widest">
            Strategisch document • Versie 2026.1
          </p>
        </header>

        {/* A. Positionering */}
        <section className="mb-16">
          <h2 className="text-xl font-bold text-brand-dark mb-4 flex items-center gap-3">
            <SafeIcon icon={FiInfo} className="text-brand-orange" /> A. Positionering
          </h2>
          <div className="prose prose-blue text-gray-600 leading-relaxed">
            <p>
              Vacature.ai is gepositioneerd als een overdraagbare AI-matching methodiek. In plaats van een gesloten software-ecosysteem, biedt het een gestructureerd framework voor de privacy-first analyse van de aansluiting tussen kandidaten en vacatures. De focus ligt op het objectiveren van kwalificaties en het inzichtelijk maken van de match op basis van feitelijke tekstuele overlap en contextuele relevantie.
            </p>
          </div>
        </section>

        {/* B. Het AI-Matching Framework */}
        <section className="mb-16">
          <h2 className="text-xl font-bold text-brand-dark mb-6 flex items-center gap-3">
            <SafeIcon icon={FiLayers} className="text-brand-orange" /> B. Het AI-Matching Framework
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {frameworkSteps.map((step, idx) => (
              <div key={idx} className="p-5 bg-gray-50 rounded-2xl border border-gray-100">
                <div className="text-xs font-bold text-brand-orange mb-2">STAP {idx + 1}</div>
                <h3 className="text-sm font-bold text-brand-dark mb-2 leading-tight">{step.title}</h3>
                <p className="text-[11px] text-gray-500 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* C. Toepassingen */}
        <section className="mb-16">
          <h2 className="text-xl font-bold text-brand-dark mb-6 flex items-center gap-3">
            <SafeIcon icon={FiTarget} className="text-brand-orange" /> C. Toepassingen
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {targetGroups.map((group, idx) => (
              <div key={idx} className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm">
                <h3 className="font-bold text-brand-dark mb-2 text-sm">{group.name}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{group.context}</p>
              </div>
            ))}
          </div>
        </section>

        {/* D. Wat Vacature.ai bewust niet is */}
        <section className="mb-16">
          <h2 className="text-xl font-bold text-brand-dark mb-6 flex items-center gap-3">
            <SafeIcon icon={FiBox} className="text-brand-orange" /> D. Wat Vacature.ai bewust niet is
          </h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
              <SafeIcon icon={FiShield} className="text-gray-400 mt-1" />
              <div>
                <h4 className="text-sm font-bold text-brand-dark mb-1">Geen ATS of kandidaten-database</h4>
                <p className="text-xs text-gray-500 leading-relaxed">Vacature.ai faciliteert geen opslag van persoonsgegevens of beheer van sollicitatie-workflows.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
              <SafeIcon icon={FiCpu} className="text-gray-400 mt-1" />
              <div>
                <h4 className="text-sm font-bold text-brand-dark mb-1">Geen beslissingsautomaat</h4>
                <p className="text-xs text-gray-500 leading-relaxed">De methodiek is adviserend van aard en behoudt de menselijke regie in het selectieproces.</p>
              </div>
            </div>
          </div>
        </section>

        {/* E. Samenwerking en licentie */}
        <section className="mb-16">
          <h2 className="text-xl font-bold text-brand-dark mb-6 flex items-center gap-3">
            <SafeIcon icon={FiBriefcase} className="text-brand-orange" /> E. Samenwerking en licentie
          </h2>
          <div className="bg-brand-dark text-white p-8 rounded-3xl">
            <p className="text-sm leading-relaxed mb-6 opacity-90">
              De methodiek achter Vacature.ai is beschikbaar voor organisaties die behoefte hebben aan een bewezen analyse-framework binnen hun eigen IT-architectuur. Dit kan variëren van kortlopende pilots tot langdurige licentie-overeenkomsten.
            </p>
            <div className="pt-6 border-t border-white/10">
              <p className="text-xs font-medium text-gray-400 leading-relaxed italic">
                Bij interesse in samenwerking...
              </p>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
};

export default StrategicOverview;